import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';
import { RouterLink } from '@angular/router';
import { IconComponent } from '../icon/icon.component';

export type AccessDeniedVariant = 'denied' | 'notFound';

/**
 * Shared empty-state shown when a record can't be displayed — either the backend denied access
 * (403) or the record doesn't exist / is no longer visible. It exists so every detail/view screen
 * degrades to the SAME clear message instead of a blank page, a generic "blocked" panel, or a
 * transient toast over empty content. The frontend never decides access itself (the server does);
 * this is purely how the denial/absence is surfaced to the user.
 *
 * `variant` picks the default icon + copy; `title` / `message` override them (e.g. to show the
 * exact reason the backend returned). `homeLink` renders an optional "way out" navigation link.
 */
@Component({
  selector: 'app-access-denied',
  standalone: true,
  imports: [IconComponent, RouterLink],
  templateUrl: './access-denied.component.html',
  styleUrl: './access-denied.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AccessDeniedComponent {
  readonly variant = input<AccessDeniedVariant>('denied');
  readonly fill = input(false);
  readonly homeLink = input<string | unknown[] | null>(null);
  readonly homeLabel = input('Back to dashboard');
  readonly title = input<string | null>(null);
  readonly message = input<string | null>(null);
  readonly icon = input<string | null>(null);

  readonly isNotFound = computed(() => this.variant() === 'notFound');

  readonly displayTitle = computed(
    () => this.title()?.trim() ?? (this.isNotFound() ? 'Not found' : 'Access denied'),
  );

  readonly displayMessage = computed(
    () =>
      this.message()?.trim() ??
      (this.isNotFound()
        ? 'This item doesn’t exist, or it’s no longer available to you.'
        : 'You don’t have permission to view this. If you think this is a mistake, contact your administrator.'),
  );

  readonly displayIcon = computed(() => this.icon()?.trim() ?? (this.isNotFound() ? 'search' : 'lock'));
}
